import { SlowestEndpoints } from '@/app/pages/Diagnostics/SlowestEndpoints'
import { SummaryCards } from '@/app/pages/Diagnostics/SummaryCards'
import { getDiagnosticsColumns } from '@/app/pages/Diagnostics/columns'
import { CLIENT_LABELS } from '@/app/pages/Diagnostics/diagnosticsUtils'
import { exportDiagnosticsAsCsv } from '@/app/pages/Diagnostics/exportUtils'
import { CommonTable } from '@/components/common-table/CommonTable'
import { type FilterOption, FiltersSearchBar } from '@/components/common-table/FilterSearchBar'
import { usePageHeaderActions, usePageHeaderTabs } from '@/components/page-context/HeaderActionsHook'
import { ToolbarExtras } from '@/components/page-context/ToolbarExtras'
import type { PageAction } from '@/components/page-context/types'
import type { OptionListOption as ComboboxOption } from '@/components/ui/option-list'
import { PageContentArea } from '@/components/ui/page-content-area'
import { Picker } from '@/components/ui/picker'
import { applyFilterLogic } from '@/hooks/applyFilterLogic'
import { type ApiMetric, useDiagnosticsStore } from '@/stores/diagnosticsStore'
import { Activity, Download, Pause, Play, Trash2 } from 'lucide-react'
import { useDeferredValue, useEffect, useMemo, useState } from 'react'

type TimeWindow = 'all' | '1m' | '5m' | '15m' | '30m' | '1h'
type ClientTab = 'all' | 'api' | 'owner'

const CLIENT_TABS: { key: ClientTab; name: string }[] = [
	{ key: 'all', name: 'All' },
	{ key: 'owner', name: CLIENT_LABELS.owner },
	{ key: 'api', name: CLIENT_LABELS.api },
]

const TIME_WINDOWS: { value: TimeWindow; label: string; ms: number | null }[] = [
	{ value: 'all', label: 'session', ms: null },
	{ value: '1m', label: 'last 1m', ms: 60_000 },
	{ value: '5m', label: 'last 5m', ms: 5 * 60_000 },
	{ value: '15m', label: 'last 15m', ms: 15 * 60_000 },
	{ value: '30m', label: 'last 30m', ms: 30 * 60_000 },
	{ value: '1h', label: 'last 1h', ms: 60 * 60_000 },
]

const TIME_WINDOW_OPTIONS: ComboboxOption[] = TIME_WINDOWS.map(w => ({
	value: w.value,
	label: `Window: ${w.label}`,
}))

const FILTER_COLUMNS = [
	{ id: 'method', label: 'Method' },
	{ id: 'status', label: 'Status' },
	{ id: 'success', label: 'Success' },
	{ id: 'duration', label: 'Duration (ms)', dataType: 'number' },
]

const COLUMN_OPERATORS: Record<string, FilterOption['operator'][]> = {
	method: ['equals', 'notEquals'],
	status: ['equals', 'notEquals', '<', '<=', '>', '>='],
	success: ['equals'],
	duration: ['>', '>=', '<', '<='],
}

const withinWindow = (metrics: ApiMetric[], ms: number | null) =>
	ms === null ? metrics : metrics.filter(m => m.timestamp >= Date.now() - ms)

const getMetrics = () => useDiagnosticsStore.getState().metrics

const getMetricValue = (metric: ApiMetric, column: string): unknown => {
	switch (column) {
		case 'method':
			return metric.method
		case 'status':
			return metric.status ?? ''
		case 'success':
			return metric.success ? 'true' : 'false'
		case 'duration':
			return metric.duration
		default:
			return undefined
	}
}

export const DiagnosticsPage = () => {
	const metrics = useDiagnosticsStore(s => s.metrics)
	const totalRequestsSeen = useDiagnosticsStore(s => s.totalRequestsSeen)
	const enabled = useDiagnosticsStore(s => s.enabled)
	const clearMetrics = useDiagnosticsStore(s => s.clearMetrics)
	const setEnabled = useDiagnosticsStore(s => s.setEnabled)

	const { setActions } = usePageHeaderActions()
	const { setTabs } = usePageHeaderTabs()

	const [search, setSearch] = useState('')
	const [filters, setFilters] = useState<FilterOption[]>([])
	const [timeWindow, setTimeWindow] = useState<TimeWindow>('all')
	const [clientFilter, setClientFilter] = useState<ClientTab>('all')

	const deferredSearch = useDeferredValue(search)

	const windowConfig = useMemo(() => TIME_WINDOWS.find(w => w.value === timeWindow) ?? TIME_WINDOWS[0], [timeWindow])

	const windowedMetrics = useMemo(() => withinWindow(metrics, windowConfig.ms), [metrics, windowConfig])

	const hasMetrics = metrics.length > 0

	const clientScopedMetrics = useMemo(() => {
		if (clientFilter === 'all') {
			return windowedMetrics
		}
		return windowedMetrics.filter(m => m.clientName === clientFilter)
	}, [windowedMetrics, clientFilter])

	const clientCounts = useMemo(() => {
		const counts: Record<ClientTab, number> = { all: windowedMetrics.length, api: 0, owner: 0 }
		for (const m of windowedMetrics) {
			if (m.clientName === 'api' || m.clientName === 'owner') {
				counts[m.clientName]++
			}
		}
		return counts
	}, [windowedMetrics])

	useEffect(() => {
		const tabs = CLIENT_TABS.map(tab => ({
			key: tab.key,
			name: tab.name,
			count: clientCounts[tab.key],
			isActive: clientFilter === tab.key,
			onClick: () => setClientFilter(tab.key),
		}))
		setTabs({ visibleTabs: tabs, allTabs: tabs })
	}, [clientCounts, clientFilter, setTabs])

	const filterableMetrics = useMemo(
		() =>
			clientScopedMetrics.map(m => ({
				...m,
				success: m.success ? 'true' : 'false',
				status: m.status ?? '',
			})) as unknown as Record<string, unknown>[],
		[clientScopedMetrics],
	)

	const filtered = useMemo(() => {
		const term = deferredSearch.trim().toLowerCase()
		const chipFiltered = applyFilterLogic(clientScopedMetrics, filters, getMetricValue)
		const textFiltered = term
			? chipFiltered.filter(m => m.url.toLowerCase().includes(term) || m.method.toLowerCase().includes(term))
			: chipFiltered
		return [...textFiltered].reverse()
	}, [clientScopedMetrics, filters, deferredSearch])

	const columns = useMemo(() => getDiagnosticsColumns(), [])

	// Metrics grow on every request, so the export handlers read the buffer at click time instead of
	// closing over it. Otherwise every tick re-registers all three actions into the shared header context.
	useEffect(() => {
		const actions: PageAction[] = [
			{
				id: 'diagnostics-toggle',
				dataTestId: 'diagnostics-toggle',
				type: 'button',
				variant: 'outline',
				label: enabled ? 'Pause' : 'Resume',
				icon: enabled ? <Pause className='size-(--icon-standalone)' /> : <Play className='size-(--icon-standalone)' />,
				tooltip: enabled ? 'Pause recording' : 'Resume recording',
				onClick: () => setEnabled(!enabled),
			},
			{
				id: 'diagnostics-clear',
				type: 'button',
				variant: 'outline',
				label: 'Clear',
				icon: <Trash2 className='size-(--icon-standalone)' />,
				disabled: !hasMetrics,
				tooltip: hasMetrics ? undefined : 'No diagnostics data to clear',
				dataTestId: 'diagnostics-clear',
				onClick: () => clearMetrics(),
			},
			{
				id: 'diagnostics-export',
				type: 'button',
				variant: 'default',
				label: 'Export CSV',
				icon: <Download className='size-(--icon-standalone)' />,
				disabled: !hasMetrics,
				tooltip: hasMetrics ? undefined : 'No diagnostics data to export',
				dataTestId: 'diagnostics-export',
				onClick: () => exportDiagnosticsAsCsv(withinWindow(getMetrics(), windowConfig.ms)),
				children: [
					{
						id: 'export-csv-window',
						dataTestId: 'diagnostics-export-window',
						label: 'Current window',
						onClick: () => exportDiagnosticsAsCsv(withinWindow(getMetrics(), windowConfig.ms)),
					},
					{
						id: 'export-csv-full',
						dataTestId: 'diagnostics-export-full',
						label: 'Full buffer',
						onClick: () => exportDiagnosticsAsCsv(getMetrics()),
					},
				],
			},
		]
		setActions(actions)
	}, [enabled, hasMetrics, windowConfig, setEnabled, clearMetrics, setActions])

	return (
		<div className='flex flex-col gap-y-4 size-full py-4'>
			<ToolbarExtras>
				<div className='flex items-center gap-2'>
					<FiltersSearchBar
						containerTestId='diagnostics-search'
						searchInputTestId='diagnostics-search-input'
						clearButtonTestId='diagnostics-search-clear'
						filterPopoverTestId='diagnostics-filter-popover'
						value={search}
						onChange={setSearch}
						placeholder='Filter by URL or method...'
						columns={FILTER_COLUMNS}
						data={filterableMetrics}
						filters={filters}
						onFiltersChange={setFilters}
						enableOperatorChange
						columnOperators={COLUMN_OPERATORS}
					/>
					<Picker
						mode='single'
						value={timeWindow}
						onValueChange={v => setTimeWindow(v as TimeWindow)}
						options={TIME_WINDOW_OPTIONS}
						className='w-44 shrink-0'
					/>
				</div>
			</ToolbarExtras>

			<SummaryCards
				metrics={clientScopedMetrics}
				totalRequestsSeen={totalRequestsSeen}
				windowLabel={windowConfig.label}
			/>

			<SlowestEndpoints metrics={clientScopedMetrics} />

			<div className='bg-card shadow overflow-hidden flex-1 min-h-0 mx-4'>
				<PageContentArea
					dataTestId='diagnostics-content'
					emptyTestId='diagnostics-empty'
					isLoading={false}
					error={null}
					dataLength={filtered.length}
					hasFilters={filters.length > 0 || deferredSearch.trim().length > 0}
					emptyIcon={Activity}
					emptyTitle='No API requests captured yet'
					emptyDescription='Requests this app makes are captured here as you use it. Move around to collect some.'
					emptyContainerHeight='100%'
					emptyShowBorder={false}
					onClearFilters={() => {
						setSearch('')
						setFilters([])
					}}>
					<CommonTable
						dataTestId='diagnostics-table'
						data={filtered}
						columns={columns}
						isLoading={false}
						error={null}
						enableSelection={false}
						getRowId={row => row.id}
					/>
				</PageContentArea>
			</div>
		</div>
	)
}
